/**
 * Owner: Masa Bayu
 * File: library-query.ts
 * Date:Time: 2026-09-16 06:40
 * Description: T4-E2 library filter, sort, and search over projected items.
 */
import { toLibraryShelfState } from './library-state-machine';
import { assertT4E1SecurityIntact } from './library-security';
export const QXREAD_LIBRARY_SORT_KEYS = [
    'recent',
    'title',
    'author',
    'progress',
    'completed',
];
export const QXREAD_LIBRARY_FILTER_KEYS = [
    'all',
    'available',
    'reading',
    'completed',
    'blocked',
    'archived',
    'update_available',
];
function authorLabel(item, authorsByPublicationId) {
    const authors = authorsByPublicationId?.get(item.publicationId);
    if (authors === undefined || authors.length === 0) {
        return '';
    }
    return authors.join(' ').toLowerCase();
}
export function filterLibraryItems(items, filter = 'all') {
    assertT4E1SecurityIntact();
    if (filter === 'all') {
        return Object.freeze(items.filter((item) => item.state !== 'removed'));
    }
    if (filter === 'update_available') {
        return Object.freeze(items.filter((item) => item.state === 'update_available'));
    }
    if (filter === 'archived') {
        return Object.freeze(items.filter((item) => item.state === 'archived'));
    }
    if (filter === 'blocked') {
        return Object.freeze(items.filter((item) => item.state === 'blocked'));
    }
    if (filter === 'completed') {
        return Object.freeze(items.filter((item) => toLibraryShelfState(item) === 'completed' && item.state !== 'archived'));
    }
    if (filter === 'reading') {
        return Object.freeze(items.filter((item) => toLibraryShelfState(item) === 'reading' && item.state !== 'archived'));
    }
    return Object.freeze(items.filter((item) => toLibraryShelfState(item) === 'available' && item.state !== 'archived'));
}
export function sortLibraryItems(items, sort = 'recent', authorsByPublicationId) {
    assertT4E1SecurityIntact();
    const copy = items.slice();
    if (sort === 'title') {
        copy.sort((a, b) => a.title.localeCompare(b.title));
    }
    else if (sort === 'author') {
        copy.sort((a, b) => authorLabel(a, authorsByPublicationId).localeCompare(authorLabel(b, authorsByPublicationId)));
    }
    else if (sort === 'progress') {
        copy.sort((a, b) => b.progress.percentComplete - a.progress.percentComplete);
    }
    else if (sort === 'completed') {
        copy.sort((a, b) => {
            const aAt = a.progress.completedAt ?? '';
            const bAt = b.progress.completedAt ?? '';
            return bAt.localeCompare(aAt);
        });
    }
    else {
        copy.sort((a, b) => {
            const aAt = a.progress.lastOpenedAt ?? a.updatedAt;
            const bAt = b.progress.lastOpenedAt ?? b.updatedAt;
            return bAt.localeCompare(aAt);
        });
    }
    return Object.freeze(copy);
}
export function searchLibraryItems(items, query, authorsByPublicationId) {
    assertT4E1SecurityIntact();
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) {
        return Object.freeze(items.slice());
    }
    return Object.freeze(items.filter((item) => {
        const hay = `${item.title} ${authorLabel(item, authorsByPublicationId)}`.toLowerCase();
        return hay.includes(needle);
    }));
}
export function queryLibraryItems(items, options = {}) {
    const filtered = filterLibraryItems(items, options.filter ?? 'all');
    const searched = searchLibraryItems(filtered, options.query ?? '', options.authorsByPublicationId);
    return sortLibraryItems(searched, options.sort ?? 'recent', options.authorsByPublicationId);
}
//# sourceMappingURL=library-query.js.map